/**
 * User activity for background polling. A tab that is open and visible but untouched must
 * stop polling the API: every request keeps the Azure web app from scaling to zero. Polls
 * run only while something is in progress, or while the user was active (pointer, keys,
 * wheel, focus) within the last {@link IDLE_AFTER_MS}; they resume on the next input or
 * when the window regains focus.
 */
import { useSyncExternalStore } from 'react';

export const IDLE_AFTER_MS = 5 * 60_000;
/** Pointer moves count at most this often (they fire at frame rate). */
const MOVE_THROTTLE_MS = 2000;

let active = true;
let lastActivity = Date.now();
let timer: ReturnType<typeof setTimeout> | undefined;
let installed = false;
let idleAfter = IDLE_AFTER_MS;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function armTimer(): void {
  clearTimeout(timer);
  timer = setTimeout(() => {
    if (!active) return;
    active = false;
    notify();
  }, idleAfter);
}

/** Records user activity (also called by the DOM listeners). */
export function markActive(now = Date.now()): void {
  lastActivity = now;
  armTimer();
  if (!active) {
    active = true;
    notify();
  }
}

function onMove(): void {
  const now = Date.now();
  if (!active || now - lastActivity > MOVE_THROTTLE_MS) markActive(now);
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') markActive();
}

function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const opts = { capture: true, passive: true } as const;
  window.addEventListener('pointerdown', () => markActive(), opts);
  window.addEventListener('keydown', () => markActive(), opts);
  window.addEventListener('wheel', () => markActive(), opts);
  window.addEventListener('pointermove', onMove, opts);
  window.addEventListener('focus', () => markActive());
  document.addEventListener('visibilitychange', onVisibility);
  armTimer();
}

/** True while the user was active within the idle window. */
export function isUserActive(): boolean {
  install();
  return active;
}

function subscribe(listener: () => void): () => void {
  install();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when the user goes idle / comes back. */
export function useUserActive(): boolean {
  return useSyncExternalStore(subscribe, isUserActive, () => true);
}

/**
 * Poll interval for a query: `busyMs` while something is running (slowed to `idleBusyMs`
 * when the user is idle), `quietMs` otherwise while active, and no polling when idle and
 * nothing runs.
 */
export function pollInterval(
  userActive: boolean,
  busy: boolean,
  {
    busyMs,
    quietMs = false,
    idleBusyMs = false,
  }: { busyMs: number; quietMs?: number | false; idleBusyMs?: number | false },
): number | false {
  if (busy) return userActive ? busyMs : idleBusyMs;
  return userActive ? quietMs : false;
}

/** Tests: sets the idle window and the current state. */
export function setActivityForTests(options: { active?: boolean; idleAfterMs?: number }): void {
  if (options.idleAfterMs !== undefined) idleAfter = options.idleAfterMs;
  if (options.active !== undefined && options.active !== active) {
    active = options.active;
    if (active) armTimer();
    notify();
  }
}
